import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";
import "./SavedSchedules.css";

const API_URL = process.env.REACT_APP_API_URL;

const SavedSchedules = ({ onNavigate }) => {
  const [savedSchedules, setSavedSchedules] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  useEffect(() => {
    fetchSavedSchedules();
  }, []);

  const fetchSavedSchedules = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/saved-schedules`);
      setSavedSchedules(response.data);
    } catch (error) {
      console.error("Fehler beim Abrufen der gespeicherten Zeitpläne:", error);
      toast.error("Fehler beim Abrufen der gespeicherten Zeitpläne");
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const sortedSchedules = [...savedSchedules].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    if (sortBy === "createdAt") {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }

    if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
    if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const handleLoad = async (id, name) => {
    const confirmed = window.confirm(
      `Dies überschreibt den aktuellen Zeitplan!\n\nEin automatisches Backup wird erstellt.\n\nMöchten Sie "${name}" wirklich laden?`
    );

    if (!confirmed) return;

    try {
      const response = await axios.post(`${API_URL}/api/saved-schedules/${id}/load`);
      toast.success(
        `Zeitplan erfolgreich geladen! Backup: ${response.data.backupName} | ${response.data.studentsRestored} Schüler, ${response.data.coachesRestored} Trainer, ${response.data.coursesRestored} Kurse wiederhergestellt`,
        { autoClose: 5000 }
      );
      if (onNavigate) onNavigate("schedule");
    } catch (error) {
      console.error("Fehler beim Laden des Zeitplans:", error);
      toast.error("Fehler beim Laden des Zeitplans");
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = window.confirm(
      `Möchten Sie den Zeitplan "${name}" wirklich löschen?\n\nDieser Vorgang kann nicht rückgängig gemacht werden.`
    );

    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/api/saved-schedules/${id}`);
      toast.success("Zeitplan erfolgreich gelöscht");
      fetchSavedSchedules();
    } catch (error) {
      console.error("Fehler beim Löschen des Zeitplans:", error);
      toast.error("Fehler beim Löschen des Zeitplans");
    }
  };

  const handleStartEdit = (schedule) => {
    setEditingId(schedule._id);
    setEditName(schedule.name);
    setEditDescription(schedule.description || "");
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API_URL}/api/saved-schedules/${editingId}`, {
        name: editName,
        description: editDescription,
      });
      toast.success("Zeitplan erfolgreich aktualisiert");
      setEditingId(null);
      fetchSavedSchedules();
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Zeitplans:", error);
      toast.error("Fehler beim Aktualisieren des Zeitplans");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditDescription("");
  };

  const handleExport = (schedule) => {
    const dataStr = JSON.stringify(schedule, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `schedule-${schedule.name}-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const text = await file.text();
        const data = JSON.parse(text);

        // Validate structure
        if (!data.students || !data.coaches || !data.schedule) {
          toast.error("Ungültiges JSON-Format! Es fehlen erforderliche Felder.");
          return;
        }

        // Create new saved schedule from imported data
        const newName = prompt("Name für den importierten Plan:", data.name || "Importierter Plan");
        if (!newName) return;

        await axios.post(`${API_URL}/api/saved-schedules`, {
          name: newName,
          description: data.description || "Importiert aus JSON-Datei",
        });

        toast.success("Zeitplan erfolgreich importiert");
        fetchSavedSchedules();
      } catch (error) {
        console.error("Fehler beim Importieren:", error);
        toast.error("Fehler beim Importieren der Datei");
      }
    };

    input.click();
  };

  return (
    <div className="saved-schedules-container">
      <h1>Gespeicherte Zeitpläne</h1>

      <div className="toolbar">
        <button className="btn-primary" onClick={handleImport}>
          📥 JSON importieren
        </button>
        <button className="btn-secondary" onClick={() => onNavigate && onNavigate("schedule")}>
          ← Zurück zum Zeitplan
        </button>
      </div>

      {savedSchedules.length === 0 ? (
        <div className="empty-state">
          <p>Keine gespeicherten Zeitpläne vorhanden.</p>
          <p>Speichern Sie einen Zeitplan aus der Schedule-Ansicht.</p>
        </div>
      ) : (
        <table className="schedules-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("name")} className="sortable">
                Name {sortBy === "name" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>
              <th>Beschreibung</th>
              <th onClick={() => handleSort("createdAt")} className="sortable">
                Erstellt am {sortBy === "createdAt" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>
              <th>Erstellt von</th>
              <th className="text-center">Schüler</th>
              <th className="text-center">Trainer</th>
              <th className="text-center">Kurse</th>
              <th className="text-center">Nicht zugewiesen</th>
              <th className="actions-col">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {sortedSchedules.map((schedule) => (
              <tr key={schedule._id}>
                <td>
                  {editingId === schedule._id ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="inline-edit"
                    />
                  ) : (
                    <strong>{schedule.name}</strong>
                  )}
                </td>
                <td>
                  {editingId === schedule._id ? (
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      className="inline-edit"
                      rows={2}
                    />
                  ) : (
                    schedule.description || "-"
                  )}
                </td>
                <td>{new Date(schedule.createdAt).toLocaleString("de-DE")}</td>
                <td>{schedule.createdByEmail || "-"}</td>
                <td className="text-center">{schedule.metadata?.studentCount || 0}</td>
                <td className="text-center">{schedule.metadata?.coachCount || 0}</td>
                <td className="text-center">{schedule.metadata?.courseCount || 0}</td>
                <td className="text-center">{schedule.metadata?.unassignedCount || 0}</td>
                <td className="actions-col">
                  {editingId === schedule._id ? (
                    <>
                      <button
                        className="btn-small btn-success"
                        onClick={handleSaveEdit}
                        title="Speichern"
                      >
                        ✓
                      </button>
                      <button
                        className="btn-small btn-cancel"
                        onClick={handleCancelEdit}
                        title="Abbrechen"
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn-small btn-primary"
                        onClick={() => handleLoad(schedule._id, schedule.name)}
                        title="Laden"
                      >
                        📂
                      </button>
                      <button
                        className="btn-small btn-secondary"
                        onClick={() => handleStartEdit(schedule)}
                        title="Bearbeiten"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-small btn-secondary"
                        onClick={() => handleExport(schedule)}
                        title="Als JSON exportieren"
                      >
                        📤
                      </button>
                      <button
                        className="btn-small btn-danger"
                        onClick={() => handleDelete(schedule._id, schedule.name)}
                        title="Löschen"
                      >
                        🗑️
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SavedSchedules;
